import { useEffect, useMemo } from 'react'

const CSS_VAR_MAP = {
  colorPrimary: '--proposal-primary',
  colorAccent: '--proposal-accent',
  colorGold: '--proposal-gold',
  colorBackground: '--proposal-bg',
  colorText: '--proposal-text',
  colorMuted: '--proposal-muted',
  fontDisplay: '--proposal-font-display',
  fontBody: '--proposal-font-body'
}

const DEFAULT_THEME = {
  colorPrimary: '#1d4d33',
  colorAccent: '#2f7350',
  colorGold: '#c19a3e',
  colorBackground: '#f7f8fa',
  colorText: '#333333',
  colorMuted: '#666666',
  fontDisplay: 'Cormorant Garamond',
  fontBody: 'DM Sans',
  logoUrl: '',
  logoWidth: 180
}

export default function useTheme (templateTheme) {
  const theme = useMemo(() => ({
    ...DEFAULT_THEME,
    ...(templateTheme || {})
  }), [templateTheme])

  // Load Google Fonts dynamically
  useEffect(() => {
    const fonts = [theme.fontDisplay, theme.fontBody].filter(Boolean)
    if (fonts.length === 0) return

    // The wght axis binds per family — a single trailing ':wght@…' only
    // applies to the last family, leaving the others stuck at weight 400.
    const families = fonts
      .map(f => `family=${f.replace(/ /g, '+')}:wght@300;400;500;600;700`)
      .join('&')
    const href = `https://fonts.googleapis.com/css2?${families}&display=swap`

    const preconnects = [
      { id: 'proposal-fonts-preconnect-api', href: 'https://fonts.googleapis.com' },
      { id: 'proposal-fonts-preconnect-static', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' }
    ]
    preconnects.forEach(({ id, href: pcHref, crossOrigin }) => {
      if (document.getElementById(id)) return
      const pc = document.createElement('link')
      pc.id = id
      pc.rel = 'preconnect'
      pc.href = pcHref
      if (crossOrigin) pc.crossOrigin = crossOrigin
      document.head.appendChild(pc)
    })

    const linkId = 'proposal-google-fonts'
    let link = document.getElementById(linkId)

    if (link) {
      link.href = href
    } else {
      link = document.createElement('link')
      link.id = linkId
      link.rel = 'stylesheet'
      link.href = href
      document.head.appendChild(link)
    }

    return () => {
      const el = document.getElementById(linkId)
      if (el) el.remove()
    }
  }, [theme.fontDisplay, theme.fontBody])

  useEffect(() => {
    const root = document.documentElement
    Object.entries(CSS_VAR_MAP).forEach(([key, cssVar]) => {
      if (theme[key]) {
        const value = key.startsWith('font')
          ? `'${theme[key]}', ${key === 'fontDisplay' ? 'Georgia, serif' : "'Segoe UI', sans-serif"}`
          : theme[key]
        root.style.setProperty(cssVar, value)
      }
    })

    return () => {
      Object.values(CSS_VAR_MAP).forEach(cssVar => {
        root.style.removeProperty(cssVar)
      })
    }
  }, [theme])

  return theme
}
